import { bodyCopyLink, Button, ColumnHeader, Divider, Icon, Permission, Table, TableColumn, ThresholdPill } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import ParseHtml from 'html-react-parser';
import styled from 'styled-components';
import { BodyCopyMedium, BodyCopyXSmallSpan } from '../../../components/common/styles/globalStyles';
import { HierarchyReportingAccessContainer } from '../../../containers/reporting/HierarchyReportingAccessContainer';
import { MinNContainer } from '../../../containers/reporting/MinNContainer';
import ContentConstants from '../../../contentConstants';
import { DistributionType, EditState, IColumn, IReportingUser, IValidationCollection, SeverityType, UserType } from '../../../models';
import { IHierarchyReportingAccessConfig } from '../../../models/IHierarchyReportingAccessConfig';
import PermissionConstants from '../../../permissionConstants';
import { ColumnHelper, ValidationCollectionHelper } from '../../../utilities';
import { Col3Col9, Section, SeverityDisplay } from '../../common';
import { ReportingUserRow } from './ReportingUserRow';

export const UserTable = styled(Table)`
	&.table thead tr th {
		min-width: 36px;
	}
	&.table tbody tr td {
		min-width: 36px;
	}
`;
export const ActionColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 36px !important;
		width: 5%;
		white-space: nowrap;
	}
`;
ActionColumn.displayName = 'ActionColumn';

export const EmailColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 220px !important;
		width: 35%;
	}
`;
EmailColumn.displayName = 'EmailColumn';

export const EmailIcon = styled(Icon)`
	padding-right: 10px;
`;
EmailIcon.displayName = 'EmailIcon';

export const EmailIconDisabled = styled(Icon)`
	padding-right: 10px;
	color: #cccccc;
`;
EmailIconDisabled.displayName = 'EmailIconDisabled';

export const OtherColumn = styled(TableColumn).attrs<{}>({
	className: 'tws-table-column',
})`
	&.tws-table-column {
		min-width: 140px !important;
		width: 30%;
	}
`;
OtherColumn.displayName = 'OtherColumn';

export const TooManyUsersError = styled.div`
	margin-top: 23px;
`;
TooManyUsersError.displayName = 'TooManyUsersError';

export const LabelTextWithCounter = styled.div`
	vertical-align: middle;
	display: inline-block;
`;
LabelTextWithCounter.displayName = 'LabelTextWithCounter';

export const DisabledRowContainer = styled.div`
	text-align: center;
	a {
		${bodyCopyLink}
	}
`;

export const SectionHandler = (props: { isSurveyAdmin?: boolean; children: any }) => {
	if (props.isSurveyAdmin) {
		return <>{props.children}</>;
	} else {
		return (
			<Section id={ContentConstants.ID_REPORTING_USERS} sectionHeader={ContentConstants.SECTIONHEADERS_REPORTING_SETUP_USERS}>
				{props.children}
			</Section>
		);
	}
};

export interface IReportingUsersProps {
	isClosed?: boolean;
	isSendingEmail?: boolean;
	isSurveyAdmin?: boolean;
	readonly?: boolean;
	reportingUsers: IReportingUser[];
	surveyId: number;
	userType: UserType;
	validationResults?: IValidationCollection;
	distributionType: DistributionType | undefined;
	AddUser: (surveyId: number) => void;
	DeleteUser: (user: IReportingUser) => void;
	NotifyUser?: (surveyId: number, user: IReportingUser) => void;
	UpdateUser: (user: IReportingUser) => void;
}

export interface IReportingUsersState {
	reportingUsers: IReportingUser[];
	showBulkModal: boolean;
	editModel?: IHierarchyReportingAccessConfig;
}

export class ReportingUsers extends React.PureComponent<IReportingUsersProps, IReportingUsersState> {
	private columns: IColumn[] = [
		{
			name: 'fname',
			displayText: ContentConstants.TABLE_HEADER_REPORTING_FIRSTNAME,
			checked: true,
			displayOrder: 2,
			disabled: false,
		},
		{
			name: 'lname',
			displayText: ContentConstants.TABLE_HEADER_REPORTING_LASTNAME,
			checked: true,
			displayOrder: 1,
			disabled: false,
		},
		{
			name: 'email',
			displayText: ContentConstants.TABLE_HEADER_REPORTING_EMAIL,
			checked: true,
			displayOrder: 3,
			disabled: false,
		},
		{
			name: 'actions',
			displayText: '',
			checked: true,
			displayOrder: 4,
			disabled: false,
		},
	];

	constructor(props: IReportingUsersProps) {
		super(props);

		this.state = {
			reportingUsers: this.props.reportingUsers.filter((user: IReportingUser) => user.editState !== EditState.Remove),
			showBulkModal: false,
		};
	}

	public componentDidUpdate(previousProps: IReportingUsersProps) {
		if (previousProps.reportingUsers !== this.props.reportingUsers) {
			const reportingUsers = this.props.reportingUsers.filter((user: IReportingUser) => user.editState !== EditState.Remove);
			this.setState({ reportingUsers });
		}
	}

	public render() {
		const { AddUser, isSurveyAdmin, readonly, validationResults, surveyId } = this.props;
		const { reportingUsers } = this.state;
		const validationHelper = new ValidationCollectionHelper(validationResults);

		return (
			<SectionHandler isSurveyAdmin={isSurveyAdmin}>
				{!isSurveyAdmin && (
					<>
						<Col3Col9 label={ContentConstants.SUBSECTIONHEADERS_INSTRUCTIONS}>
							<div>{ParseHtml(ContentConstants.INSTRUCTIONALTEXT_REPORTING_USERS)}</div>
						</Col3Col9>
						<Divider type='dashed' />
					</>
				)}

				{this.getMinNContent()}
				<Col3Col9
					label={ContentConstants.SUBSECTIONHEADERS_REPORTING_REPORTING_USERS_INDIVIDUALLY}
					subLabelDisplayData={<ThresholdPill count={reportingUsers.length} threshold={50} />}
				>
					{reportingUsers.length > 0 && (
						<>
							<BodyCopyMedium>
								{ParseHtml(
									isSurveyAdmin
										? ContentConstants.INSTRUCTIONALTEXT_REPORTING_USERS_INDIVIDUAL_ADMIN
										: ContentConstants.INSTRUCTIONALTEXT_REPORTING_USERS_INDIVIDUAL,
								)}
							</BodyCopyMedium>
							<UserTable responsive={true} lastalign='right'>
								<ColumnHeader columns={ColumnHelper.GetColumnHeaders(this.columns)} />
								<tbody>
									{reportingUsers.map((user: IReportingUser, key: any) => (
										<ReportingUserRow
											key={key}
											{...this.props}
											AddUser={() => AddUser(surveyId)}
											user={user}
											index={user.id}
											validationResults={validationHelper.GetValidationResultForProperty('ReportingUsers')}
										/>
									))}
								</tbody>
							</UserTable>
						</>
					)}
					<div id='add-user'>
						<Button onClick={AddUser} floating={true} disabled={readonly} size='xs'>
							<Icon name='add' />
						</Button>
						<BodyCopyXSmallSpan style={{ paddingLeft: '10px' }}>{ContentConstants.PLACEHOLDER_REPORTING_ADD_USER}</BodyCopyXSmallSpan>
					</div>
					{reportingUsers.length > 50 && (
						<TooManyUsersError>
							<SeverityDisplay
								severity={SeverityType.Error}
								showSeverityPrefix={true}
								message={
									isSurveyAdmin
										? ContentConstants.PLACEHOLDER_REPORTING_ERROR_TOO_MANY_ADMIN
										: ContentConstants.PLACEHOLDER_REPORTING_ERROR_TOO_MANY
								}
							/>
						</TooManyUsersError>
					)}
				</Col3Col9>
				<Permission {...PermissionConstants.HIERARCHYREPORTING_ALL}>
					{this.props.distributionType !== undefined && (
						<HierarchyReportingAccessContainer
							surveyId={surveyId}
							distributionType={this.props.distributionType}
							isSurveyAdmin={isSurveyAdmin}
						/>
					)}
				</Permission>
			</SectionHandler>
		);
	}

	private getMinNContent() {
		if (this.props.userType === UserType.Internal && !this.props.isSurveyAdmin) {
			return (
				<>
					<MinNContainer {...this.props} readOnly={false} />
					<Divider type='dashed' />
				</>
			);
		}
	}
}
